import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell, ExternalLink } from 'lucide-react'
import { alertsApi, type AlertEvent } from '../lib/funnel-api'

const SEVERITY_DOT: Record<string, string> = {
  critical: 'bg-red-500',
  warning: 'bg-amber-500',
  info: 'bg-blue-400',
}

function timeAgo(dateStr: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

export default function AlertsBell() {
  const navigate = useNavigate()
  const [unreadCount, setUnreadCount] = useState(0)
  const [open, setOpen] = useState(false)
  const [recentAlerts, setRecentAlerts] = useState<AlertEvent[]>([])
  const [loadingAlerts, setLoadingAlerts] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  // Poll unread count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const response = await alertsApi.unreadCount()
        const count = response.data?.count ?? 0
        setUnreadCount(count)
      } catch {
        // API failure — badge won't show until next poll succeeds
      }
    }

    fetchUnreadCount()
    const interval = setInterval(fetchUnreadCount, 30000)
    return () => clearInterval(interval)
  }, [])

  // Load recent alerts when dropdown opens
  useEffect(() => {
    if (!open) return
    const fetchAlerts = async () => {
      setLoadingAlerts(true)
      try {
        const response = await alertsApi.listEvents()
        setRecentAlerts(Array.isArray(response.data) ? response.data.slice(0, 8) : [])
      } catch {
        // Silently fail — dropdown will show empty state
      } finally {
        setLoadingAlerts(false)
      }
    }
    fetchAlerts()
  }, [open])

  // Close on click outside
  useEffect(() => {
    if (!open) return
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  // Close on Escape
  useEffect(() => {
    if (!open) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [open])

  const handleMarkAllRead = useCallback(async () => {
    const unreadAlerts = recentAlerts.filter((a) => !a.isRead)
    await Promise.allSettled(unreadAlerts.map((a) => alertsApi.markRead(a.id)))
    setRecentAlerts((prev) => prev.map((a) => ({ ...a, isRead: true })))
    // Re-fetch actual unread count from server — the dropdown only shows up to 8
    // alerts, so there may be more unread alerts beyond the visible ones.
    try {
      const response = await alertsApi.unreadCount()
      setUnreadCount(response.data?.count ?? 0)
    } catch {
      // Optimistic fallback: subtract the ones we just marked
      setUnreadCount((prev) => Math.max(0, prev - unreadAlerts.length))
    }
  }, [recentAlerts])

  const handleMarkRead = useCallback(async (alertId: string) => {
    try {
      await alertsApi.markRead(alertId)
      setRecentAlerts((prev) => prev.map((a) => a.id === alertId ? { ...a, isRead: true } : a))
      setUnreadCount((prev) => Math.max(0, prev - 1))
    } catch {
      // Silently fail
    }
  }, [])

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="relative p-2 text-default-500 hover:text-foreground hover:bg-content2 rounded-lg transition-colors"
        aria-label="View alerts"
        aria-expanded={open}
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute top-1.5 right-1.5 h-2 w-2 bg-red-500 rounded-full ring-2 ring-white dark:ring-gray-900" />
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-[22rem] bg-tf-bg-card rounded-lg shadow-lg border border-tf-border z-50 animate-scale-in">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-tf-border">
            <div className="flex items-center gap-2.5">
              <span className="text-sm font-semibold text-tf-text">Notifications</span>
              {unreadCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[11px] font-bold text-white bg-red-500 rounded-full leading-none">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={handleMarkAllRead}
                className="text-xs font-medium text-tf-interactive hover:underline transition-colors"
              >
                Mark all read
              </button>
            )}
          </div>

          {/* Scrollable list */}
          <div className="max-h-[21rem] overflow-y-auto">
            {loadingAlerts ? (
              <div className="flex items-center justify-center py-10">
                <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : recentAlerts.length === 0 ? (
              <div className="py-10 text-center">
                <Bell className="w-8 h-8 text-tf-text-muted mx-auto mb-2 opacity-40" />
                <p className="text-tf-text-muted text-sm">No notifications yet</p>
              </div>
            ) : (
              recentAlerts.map((alert, i) => (
                <button
                  key={alert.id}
                  type="button"
                  onClick={() => { if (!alert.isRead) handleMarkRead(alert.id) }}
                  className={`group w-full flex items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-tf-bg-hover ${
                    !alert.isRead ? 'bg-tf-bg-selected' : ''
                  } ${i > 0 ? 'border-t border-tf-border' : ''}`}
                >
                  {/* Severity dot */}
                  <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${
                    !alert.isRead
                      ? (SEVERITY_DOT[alert.severity] || 'bg-blue-400')
                      : 'bg-gray-300 dark:bg-gray-600'
                  }`} />

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className={`text-[11px] font-semibold uppercase tracking-wide ${
                        !alert.isRead
                          ? (alert.severity === 'critical' ? 'text-red-500' : alert.severity === 'warning' ? 'text-amber-500' : 'text-blue-500')
                          : 'text-tf-text-muted'
                      }`}>
                        {alert.severity}
                      </span>
                      <span className="text-[11px] text-tf-text-muted shrink-0">{timeAgo(alert.createdAt)}</span>
                    </div>
                    <p className={`text-sm mt-0.5 line-clamp-2 ${!alert.isRead ? 'text-tf-text font-medium' : 'text-tf-text-sub'}`}>
                      {alert.message}
                    </p>
                  </div>
                </button>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-tf-border">
            <button
              type="button"
              onClick={() => { setOpen(false); navigate('/funnels/alerts') }}
              className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 text-xs font-medium text-tf-text-sub hover:bg-tf-bg-hover hover:text-tf-text transition-colors rounded-b-lg"
            >
              View all notifications
              <ExternalLink className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
